import { useMemo, useState } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

// project imports
import MainCard from 'components/MainCard';
import AnalyticEcommerce from 'components/cards/statistics/AnalyticEcommerce';
import OrdersTable from 'sections/dashboard/default/OrdersTable';
import API_BASE_URL from 'config/api';
import useGet from 'hooks/useGet';

const DEFAULT_CURRENCY = 'INR';

const formatNumber = (value, maximumFractionDigits = 0) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits }).format(Number(value || 0));

const formatCurrency = (currency, value) => {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || DEFAULT_CURRENCY,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${currency || DEFAULT_CURRENCY} ${amount.toFixed(2)}`;
  }
};

const renderCardFooter = (label, value, color) => (
  <Typography variant="caption" color="text.secondary">
    {label}{' '}
    <Typography component="span" variant="caption" sx={{ color }}>
      {value}
    </Typography>
  </Typography>
);

const resolveOverviewPayload = (payload) => {
  if (payload?.success) return payload;
  if (payload?.data?.success) return payload.data;
  return null;
};

const getCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.products || payload?.data || payload?.items || [];
};

const resolveProductImageUrl = (product) => {
  const raw =
    product?.image ||
    product?.imageUrl ||
    product?.thumbnail ||
    product?.thumbnailUrl ||
    product?.featuredImage ||
    product?.coverImage ||
    product?.heroImage ||
    product?.productImage ||
    product?.media?.heroImage ||
    product?.media?.galleryImages?.[0] ||
    product?.galleryImages?.[0] ||
    product?.images?.[0]?.url ||
    product?.images?.[0]?.path ||
    product?.images?.[0];

  if (!raw || typeof raw !== 'string') return '';

  const value = raw.trim().replace(/\\/g, '/');
  if (!value) return '';
  if (/^(https?:|blob:|data:)/i.test(value)) return value;

  const normalized = value.replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) return `${API_BASE_URL}/${normalized}`;
  if (normalized.startsWith('media/')) return `${API_BASE_URL}/uploads/${normalized}`;
  if (value.startsWith('/')) return `${API_BASE_URL}${value}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const createProductLookup = (products) => {
  const lookup = new Map();

  getCollection(products).forEach((product) => {
    [
      product?._id,
      product?.id,
      product?.productId,
      product?.sku,
      product?.SKU,
      product?.name,
      product?.title,
      product?.productName
    ].forEach((key) => {
      if (key) lookup.set(String(key).toLowerCase(), product);
    });
  });

  return lookup;
};

const findCatalogProduct = (lookup, item) =>
  [item?.productId, item?._id, item?.id, item?.sku, item?.SKU, item?.name, item?.title, item?.productName].reduce(
    (match, key) => match || (key ? lookup.get(String(key).toLowerCase()) : null),
    null
  );

const normalizeProductAnalyticsRow = (item, catalogProduct = {}) => {
  const orders = Number(item?.orders || 0);
  const views = Number(item?.views || 0);
  const cartAdds = Number(item?.cartAdds || 0);
  const wishlists = Number(item?.wishlists || 0);

  return {
    ...catalogProduct,
    ...item,
    productId: item?.productId || item?._id || item?.id || catalogProduct?._id || catalogProduct?.id || '',
    sku: item?.sku || item?.SKU || catalogProduct?.sku || catalogProduct?.SKU || '',
    name:
      item?.name ||
      item?.title ||
      item?.productName ||
      catalogProduct?.name ||
      catalogProduct?.title ||
      catalogProduct?.productName ||
      'Unnamed product',
    views,
    cartAdds,
    wishlists,
    orders,
    total: Number(item?.total ?? orders + views + cartAdds + wishlists)
  };
};

const buildTopProductAnalyticsRows = (productAnalytics, productLookup) => {
  if (Array.isArray(productAnalytics.products)) {
    return productAnalytics.products.map((item) => normalizeProductAnalyticsRow(item, findCatalogProduct(productLookup, item)));
  }

  return (Array.isArray(productAnalytics.mostOrdered) ? productAnalytics.mostOrdered : []).map((item) =>
    normalizeProductAnalyticsRow(
      {
        ...item,
        orders: item?.orders ?? item?.count ?? 0,
        views: item?.views ?? 0,
        cartAdds: item?.cartAdds ?? 0,
        wishlists: item?.wishlists ?? 0
      },
      findCatalogProduct(productLookup, item)
    )
  );
};

function ProductAnalyticsRow({ product }) {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = resolveProductImageUrl(product);
  const showImage = imageUrl && !imageFailed;
  const metrics = [
    { label: 'Views', value: product.views },
    { label: 'Wishlist', value: product.wishlists },
    { label: 'Cart', value: product.cartAdds },
    { label: 'Orders', value: product.orders }
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
        alignItems: 'center',
        gap: { xs: 2, md: 3 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.5, sm: 2 },
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)'
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: { xs: 72, sm: 88 },
            height: { xs: 72, sm: 88 },
            flex: '0 0 auto',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#f6f2ed',
            border: '1px solid',
            borderColor: 'divider',
            display: 'grid',
            placeItems: 'center'
          }}
        >
          {showImage ? (
            <Box
              component="img"
              src={imageUrl}
              alt={product.name || 'Product image'}
              onError={() => setImageFailed(true)}
              sx={{ width: 1, height: 1, objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Typography variant="h4" color="text.secondary">
              {(product.name || 'P').charAt(0).toUpperCase()}
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {product.name || 'Unnamed product'}
          </Typography>
          <Chip
            size="small"
            label={`SKU: ${product.sku || '-'}`}
            sx={{
              mt: 1,
              height: 28,
              borderRadius: 999,
              bgcolor: '#dcfce7',
              color: '#047857',
              fontWeight: 600,
              '& .MuiChip-label': { px: 1.25 }
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(72px, 1fr))' },
          gap: { xs: 1.5, md: 3.5 },
          minWidth: { md: 380 }
        }}
      >
        {metrics.map((metric) => (
          <Box key={metric.label} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>
              {formatNumber(metric.value)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {metric.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TopProductAnalytics({ products }) {
  return (
    <MainCard
      title="Product Analytics"
      subheader="Top 5 products by orders."
      secondary={
        <Chip
          clickable
          component={RouterLink}
          to="/analytics/events"
          label="View all"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      }
      content={false}
    >
      <Stack spacing={1.5} sx={{ p: { xs: 1.25, sm: 2 } }}>
        {products.length ? (
          products.map((product) => <ProductAnalyticsRow key={product.productId || product.sku || product.name} product={product} />)
        ) : (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No product analytics found.</Typography>
          </Box>
        )}
      </Stack>
    </MainCard>
  );
}

// ==============================|| DASHBOARD - DEFAULT ||============================== //

export default function DashboardDefault() {
  const overviewEndpoint = useMemo(() => {
    const params = new URLSearchParams({
      currency: DEFAULT_CURRENCY
    });
    return `/api/admin/dashboard/overview?${params.toString()}`;
  }, []);
  const { data, error: overviewError, loading: overviewLoading } = useGet(overviewEndpoint);
  const {
    data: productAnalyticsResponse,
    error: productAnalyticsError,
    loading: productAnalyticsLoading
  } = useGet('/api/events/analytics/products?limit=all');
  const { data: productCatalogResponse, loading: productCatalogLoading } = useGet('/api/products/?limit=all');
  const overview = resolveOverviewPayload(data);
  const currency = overview?.filters?.currency || DEFAULT_CURRENCY;
  const cards = overview?.cards || {};
  const productAnalytics = useMemo(() => productAnalyticsResponse?.data || {}, [productAnalyticsResponse]);
  const productLookup = useMemo(() => createProductLookup(productCatalogResponse), [productCatalogResponse]);
  const topProductAnalytics = useMemo(() => {
    const rows = buildTopProductAnalyticsRows(productAnalytics, productLookup);

    return rows.sort((first, second) => Number(second.orders || 0) - Number(first.orders || 0)).slice(0, 5);
  }, [productAnalytics, productLookup]);

  return (
    <Grid container rowSpacing={{ xs: 2.25, sm: 3, md: 4.5 }} columnSpacing={{ xs: 1.5, sm: 2.75 }}>
      <Grid sx={{ mb: { xs: -0.75, sm: -2.25 } }} size={12}>
        <Typography variant="h5">Dashboard</Typography>
      </Grid>

      {overviewLoading || productAnalyticsLoading || productCatalogLoading ? (
        <Grid size={12}>
          <LinearProgress />
        </Grid>
      ) : null}

      {overviewError ? (
        <Grid size={12}>
          <Alert severity="error">Failed to load dashboard overview. Showing available live sections only.</Alert>
        </Grid>
      ) : null}
      {productAnalyticsError ? (
        <Grid size={12}>
          <Alert severity="error">Failed to load product analytics.</Alert>
        </Grid>
      ) : null}

      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AnalyticEcommerce
          title="Total Page Views"
          count={formatNumber(cards?.totalPageViews?.total)}
          percentage={cards?.totalPageViews?.changePercent}
          isLoss={Number(cards?.totalPageViews?.changePercent || 0) < 0}
          color={Number(cards?.totalPageViews?.changePercent || 0) < 0 ? 'warning' : 'success'}
          footer={renderCardFooter('This year', formatNumber(cards?.totalPageViews?.currentPeriod), 'success.main')}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AnalyticEcommerce
          title="Total Users"
          count={formatNumber(cards?.totalUsers?.total)}
          percentage={cards?.totalUsers?.changePercent}
          isLoss={Number(cards?.totalUsers?.changePercent || 0) < 0}
          color={Number(cards?.totalUsers?.changePercent || 0) < 0 ? 'warning' : 'success'}
          footer={renderCardFooter('This year', formatNumber(cards?.totalUsers?.currentPeriod), 'success.main')}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AnalyticEcommerce
          title="Total Order"
          count={formatNumber(cards?.totalOrders?.total)}
          percentage={cards?.totalOrders?.changePercent}
          isLoss={Number(cards?.totalOrders?.changePercent || 0) < 0}
          color={Number(cards?.totalOrders?.changePercent || 0) < 0 ? 'warning' : 'success'}
          footer={renderCardFooter('This year', formatNumber(cards?.totalOrders?.currentPeriod), 'success.main')}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <AnalyticEcommerce
          title="Total Sales"
          count={formatCurrency(currency, cards?.totalSales?.total)}
          percentage={cards?.totalSales?.changePercent}
          isLoss={Number(cards?.totalSales?.changePercent || 0) < 0}
          color={Number(cards?.totalSales?.changePercent || 0) < 0 ? 'warning' : 'success'}
          footer={renderCardFooter('This year', formatCurrency(currency, cards?.totalSales?.currentPeriod), 'success.main')}
        />
      </Grid>

      <Grid sx={{ display: { sm: 'none', md: 'block', lg: 'none' } }} size={{ md: 8 }} />

      <Grid size={{ xs: 12, md: 12, lg: 12 }} />

      <Grid size={12}>
        <TopProductAnalytics products={topProductAnalytics} />
      </Grid>

      <Grid size={12}>
        <MainCard content={false} sx={{ overflow: 'hidden' }}>
          <OrdersTable />
        </MainCard>
      </Grid>
    </Grid>
  );
}
